// src/pages/admin/AdminDashboard.js
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import {
  UsersIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  VideoCameraIcon,
  ChartBarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';

const AdminDashboard = () => {
  const { data: analytics, isLoading } = useQuery({
    queryKey: ['admin-analytics'],
    queryFn: () => apiService.getDashboardAnalytics(),
  });

  const stats = [
    {
      name: 'Total Users',
      value: analytics?.total_users || 0,
      change: analytics?.user_growth_percentage || null,
      changeType: 'positive',
      icon: UsersIcon,
      link: '/admin/users',
    },
    {
      name: 'Admin',
      value: analytics?.admin_count || 0,
      change: analytics?.admin_growth_percentage || null,
      changeType: 'positive',
      icon: UsersIcon,
      link: '/admin/users',
    },
    {
      name: 'Candidate',
      value: analytics?.candidate_count || 0,
      change: analytics?.candidate_growth_percentage || null,
      changeType: 'positive',
      icon: UsersIcon,
      link: '/admin/users',
    },
    {
      name: 'Total Resumes',
      value: analytics?.total_resumes || 0,
      change: analytics?.resume_growth_percentage || null,
      changeType: 'positive',
      icon: ClipboardDocumentListIcon,
      link: '/admin/resumes',
    },
    {
      name: 'Total Interviews',
      value: analytics?.total_interviews || 0,
      change: analytics?.interview_growth_percentage || null,
      changeType: 'positive',
      icon: VideoCameraIcon,
      link: '/admin/interviews',
    },
  ];

  const quickActions = [
    {
      title: 'Create Interview',
      description: 'Set up a new interview with AI-generated questions',
      icon: VideoCameraIcon,
      link: '/admin/interviews',
      color: 'bg-blue-500',
    },
    {
      title: 'Upload Resumes',
      description: 'Bulk upload and rate candidate resumes',
      icon: ClipboardDocumentListIcon,
      link: '/admin/resumes',
      color: 'bg-green-500',
    },
    {
      title: 'Manage Users',
      description: 'View and manage user accounts and permissions',
      icon: UsersIcon,
      link: '/admin/users',
      color: 'bg-purple-500',
    },
    {
      title: 'View Analytics',
      description: 'Check performance metrics and insights',
      icon: ChartBarIcon,
      link: '/admin/analytics',
      color: 'bg-orange-500',
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="text-gray-600">Welcome back! Here's what's happening with your interview system.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.link}
            className="card hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <stat.icon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-4">
          <Link
            to="/admin/users"
            className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
          >
            <UsersIcon className="h-5 w-5 text-gray-600 mr-2" />
            <span className="text-sm font-medium text-gray-700">Users</span>
          </Link>
          <Link
            to="/admin/jobs"
            className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
          >
            <DocumentTextIcon className="h-5 w-5 text-gray-600 mr-2" />
            <span className="text-sm font-medium text-gray-700">Job Descriptions</span>
          </Link>
          <Link
            to="/admin/resumes"
            className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
          >
            <ClipboardDocumentListIcon className="h-5 w-5 text-gray-600 mr-2" />
            <span className="text-sm font-medium text-gray-700">Resume Evaluation</span>
          </Link>
          <Link
            to="/admin/interviews"
            className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
          >
            <VideoCameraIcon className="h-5 w-5 text-gray-600 mr-2" />
            <span className="text-sm font-medium text-gray-700">Interview Management</span>
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Interviews */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Recent Interviews</h3>
            <Link to="/admin/interviews" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              View all
            </Link>
          </div>
          <div className="space-y-3">
            {analytics?.recent_interviews?.length > 0 ? (
              analytics.recent_interviews.slice(0, 3).map((interview) => (
                <div key={interview.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{interview.title || 'Interview'}</p>
                    <p className="text-xs text-gray-500">
                      {interview.candidate_name ? `Scheduled for ${interview.candidate_name}` : 'No candidate assigned'}
                    </p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    interview.status === 'completed' ? 'bg-green-100 text-green-800' :
                    interview.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {interview.status || 'Unknown'}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No recent interviews</p>
            )}
          </div>
        </div>

        {/* Recent Users */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Recent Users</h3>
            <Link to="/admin/users" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              View all
            </Link>
          </div>
          <div className="space-y-3">
            {analytics?.recent_users?.length > 0 ? (
              analytics.recent_users.slice(0, 3).map((user) => (
                <div key={user.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <div className="flex items-center">
                    <img
                      className="h-8 w-8 rounded-full"
                      src={user.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'User')}&background=3b82f6&color=fff`}
                      alt={user.name || 'User'}
                    />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{user.name || 'Unknown User'}</p>
                      <p className="text-xs text-gray-500">{user.email || 'No email'}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.role === 'candidate' ? 'bg-blue-100 text-blue-800' :
                    user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {user.role || 'User'}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No recent users</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
        <div className="space-y-4">
          {analytics?.recent_activity?.length > 0 ? (
            analytics.recent_activity.map((activity, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                <div>
                  <p className="text-sm font-medium text-gray-900">{activity.action || 'Activity'}</p>
                  <p className="text-xs text-gray-500">{activity.user || activity.user_email || 'Unknown user'}</p>
                </div>
                <span className="text-xs text-gray-500">{activity.time || activity.created_at || 'Unknown time'}</span>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No recent activity</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;